/**
 * A component for selecting a geographic region and selecting a school
 **/
import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import * as React from "react";
import {useEffect} from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import type {OptionItem} from "@khanacademy/wonder-blocks-dropdown";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import {CircularSpinner} from "@khanacademy/wonder-blocks-progress-spinner";
import {spacing} from "@khanacademy/wonder-blocks-tokens";
import {useFlagOrCookie} from "@repo/analytics-growthbook";
import {usePublishAnalyticsEvent} from "@repo/analytics-learning-time";
import {doNotTranslate} from "@repo/i18n";
import {RequestInfo} from "@repo/shared-core";

import {
    ADMIN_LEVEL_ONE_CODES_TO_NAMES as BANGLADESH_ADMIN_LEVEL_ONE_CODES_TO_NAMES,
    ADMIN_LEVELS as BANGLADESH_ADMIN_LEVELS,
} from "#class-shared-package/bangladesh.ts";
import {
    ADMIN_LEVEL_ONE_CODES_TO_NAMES as BRAZIL_ADMIN_LEVEL_ONE_CODES_TO_NAMES,
    ADMIN_LEVELS as BRAZIL_ADMIN_LEVELS,
} from "#class-shared-package/brazil.ts";
import {
    ADMIN_LEVEL_ONE_CODES_TO_NAMES as INDIA_ADMIN_LEVEL_ONE_CODES_TO_NAMES,
    ADMIN_LEVELS as INDIA_ADMIN_LEVELS,
} from "#class-shared-package/india.ts";
import {
    ADMIN_LEVEL_ONE_CODES_TO_NAMES as KENYA_ADMIN_LEVEL_ONE_CODES_TO_NAMES,
    ADMIN_LEVELS as KENYA_ADMIN_LEVELS,
} from "#class-shared-package/kenya.ts";
import {
    ADMIN_LEVEL_ONE_CODES_TO_NAMES as PAKISTAN_ADMIN_LEVEL_ONE_CODES_TO_NAMES,
    ADMIN_LEVELS as PAKISTAN_ADMIN_LEVELS,
} from "#class-shared-package/pakistan.ts";
import {
    ADMIN_LEVEL_ONE_CODES_TO_NAMES as PERU_ADMIN_LEVEL_ONE_CODES_TO_NAMES,
    ADMIN_LEVELS as PERU_ADMIN_LEVELS,
} from "#class-shared-package/peru.ts";
import {
    ADMIN_LEVEL_ONE_CODES_TO_NAMES as PHILIPPINES_ADMIN_LEVEL_ONE_CODES_TO_NAMES,
    ADMIN_LEVELS as PHILIPPINES_ADMIN_LEVELS,
} from "#class-shared-package/philippines.ts";
import {
    ADMIN_LEVEL_ONE_CODES_TO_NAMES as VIETNAM_ADMIN_LEVEL_ONE_CODES_TO_NAMES,
    ADMIN_LEVELS as VIETNAM_ADMIN_LEVELS,
} from "#class-shared-package/vietnam.ts";

import {CountryDropdown} from "./country-dropdown.tsx";
import type {School} from "./types.ts";

export type OptionItemArray = Array<
    | React.ReactElement<React.ComponentProps<typeof OptionItem>>
    | false
    | null
    | undefined
>;

export type SchoolFilterPanelProps = {
    /**
     * The currently selected school.
     */
    selectedSchool: School | null | undefined;
    /**
     * A callback to be called when the selected school changes.
     */
    onSchoolChanged: (school?: School | null | undefined) => void;
    /**
     * TODO: Document this prop.
     */
    partnershipOnly?: boolean;
    /**
     * Whether to use the compact layout. This will hide the labels and reduce
     * the spacing between elements. The fields label text will be set to the
     * placeholder text, and aria-label will be set to the same for
     * accessibility.
     */
    compactLayout?: boolean;
    /**
     * Whether this component is being used in the sign up flow. For sign up
     * specifically we send an additional CEDAR event when the user selects a
     * school.
     */
    isSignupFlow?: boolean;
};

const USSchoolFilterPanel = React.lazy(
    () => import("./us-school-filter-panel.tsx"),
);
const LolSchoolFilterPanel = React.lazy(
    () => import("./lol-school-filter-panel.tsx"),
);
const SchoolFilterPanel = React.lazy(() => import("./school-filter-panel.tsx"));

type SchoolSelectorProps = {
    /**
     * The currently selected school.
     */
    selectedSchool: School | null | undefined;
    /**
     * A callback to be called when the selected school changes.
     */
    onSchoolChanged: (school?: School | null | undefined) => void;
    /**
     * Whether to hide the country dropdown.
     */
    hideCountryDropdown?: boolean;
    /**
     * TODO: Document this prop.
     */
    partnershipOnly?: boolean;
    /**
     * Whether to use the compact layout. This will hide the labels and reduce
     * the spacing between elements. The fields label text will be set to the
     * placeholder text, and aria-label will be set to the same for
     * accessibility.
     */
    compactLayout?: boolean;
    /**
     * Whether to show the country flag in the country dropdown.
     */
    showCountryFlag?: boolean;
    /**
     * Whether this component is being used in the sign up flow. For sign up
     * specifically we send an additional CEDAR event when the user selects a
     * school.
     */
    isSignupFlow?: boolean;
} & LabeledCountryDropdownProps;

type LabeledCountryDropdownProps = {
    selectedCountryCode: string | null | undefined;
    onCountryChanged: (countryCode?: string | null | undefined) => void;
};

function SchoolSelector(props: SchoolSelectorProps): React.ReactElement {
    const {
        selectedCountryCode,
        selectedSchool,
        onSchoolChanged,
        onCountryChanged,
        hideCountryDropdown,
        partnershipOnly,
        compactLayout,
        showCountryFlag,
        isSignupFlow,
    } = props;
    const publishAnalyticsEvent = usePublishAnalyticsEvent();
    const showVietnamFilter = useFlagOrCookie("vietnam-school-picker");
    const showKenyaFilter = useFlagOrCookie("kenya-school-picker");

    let selectedCountry = selectedCountryCode || selectedSchool?.country;
    // If we do not have an existing country or school affiliation for this
    // user, we should attempt to initialize the school picker modal with
    // the country they are using the site from. We must make sure, though,
    // that we are only setting this value in the instance where we are
    // able to infer the country.
    if (!selectedCountry && RequestInfo.remoteCountry !== "ZZ") {
        selectedCountry = RequestInfo.remoteCountry;
        onCountryChanged(selectedCountry);
    }

    useEffect(() => {
        if (selectedCountry) {
            publishAnalyticsEvent({
                eventType: "SchoolPickerCountrySelected",
                eventSchemaVersion: 2,
                countryCode: selectedCountry,
            });
        }
    }, [publishAnalyticsEvent, selectedCountry]);

    return (
        <View
            style={[
                styles.schoolSelector,
                compactLayout ? styles.compactLayout : null,
            ]}
        >
            {!hideCountryDropdown && (
                <View>
                    {/* Country dropdown is outside not a part of the
                    FilterPanel components below so that when the country
                    changes, it does not become unfocused because it's a
                    different component. */}
                    <CountryDropdown
                        value={selectedCountry}
                        onChange={onCountryChanged}
                        compactLayout={compactLayout}
                        showCountryFlag={showCountryFlag}
                    />
                    {!compactLayout && <Strut size={spacing.large_24} />}
                </View>
            )}
            {/* sync-start:school-picker-countries 1245875172 services/static/apps/khanacademy/src/settings-package/school-section-helpers.ts */}
            {/* sync-start:school-picker-countries 1734594658 services/static/apps/khanacademy/src/auth/components/school-selector/school-selector-query.ts */}
            <React.Suspense fallback={<CircularSpinner size="small" />}>
                {selectedCountry === "US" && (
                    <USSchoolFilterPanel
                        selectedSchool={selectedSchool}
                        onSchoolChanged={onSchoolChanged}
                        compactLayout={compactLayout}
                        isSignupFlow={isSignupFlow}
                    />
                )}
                {selectedCountry === "BD" && (
                    <SchoolFilterPanel
                        selectedSchool={selectedSchool}
                        onSchoolChanged={onSchoolChanged}
                        partnershipOnly={partnershipOnly}
                        compactLayout={compactLayout}
                        isSignupFlow={isSignupFlow}
                        countryCode="BD"
                        adminHierarchy={{
                            root: BANGLADESH_ADMIN_LEVELS,
                            levelOneLabel: t`Division`,
                            levelOnePlaceholder: doNotTranslate`e.g., DHAKA`,
                            levelTwoLabel: t`District`,
                            levelTwoPlaceholder: doNotTranslate`e.g., GAZIPUR`,
                            localityLabel: t`City`,
                            localityPlaceholder: doNotTranslate`e.g., KAPASIA`,
                        }}
                        levelOneCodesToNames={
                            BANGLADESH_ADMIN_LEVEL_ONE_CODES_TO_NAMES
                        }
                    />
                )}
                {selectedCountry === "BR" && (
                    <SchoolFilterPanel
                        selectedSchool={selectedSchool}
                        onSchoolChanged={onSchoolChanged}
                        partnershipOnly={partnershipOnly}
                        compactLayout={compactLayout}
                        isSignupFlow={isSignupFlow}
                        countryCode="BR"
                        adminHierarchy={{
                            root: BRAZIL_ADMIN_LEVELS,
                            levelOneLabel: t`State `,
                            levelOnePlaceholder: doNotTranslate`e.g., AMAZONAS`,
                            localityLabel: t`City`,
                            localityPlaceholder: doNotTranslate`e.g., SÃO PAULO`,
                        }}
                        levelOneCodesToNames={
                            BRAZIL_ADMIN_LEVEL_ONE_CODES_TO_NAMES
                        }
                    />
                )}
                {selectedCountry === "IN" && (
                    <SchoolFilterPanel
                        selectedSchool={selectedSchool}
                        onSchoolChanged={onSchoolChanged}
                        partnershipOnly={partnershipOnly}
                        compactLayout={compactLayout}
                        isSignupFlow={isSignupFlow}
                        countryCode="IN"
                        adminHierarchy={{
                            root: INDIA_ADMIN_LEVELS,
                            // Yes the string has a space at the end. This is how it always has
                            // been. If it's removed we'll need to get it retranslated.
                            levelOneLabel: t`State `,
                            levelOnePlaceholder: t`e.g., Mahārāshtra`,
                            levelTwoLabel: t`District`,
                            levelTwoPlaceholder: t`e.g., PUNE`,
                            localityLabel: t`Block`,
                            localityPlaceholder: t`e.g., PURANDAR`,
                        }}
                        levelOneCodesToNames={
                            INDIA_ADMIN_LEVEL_ONE_CODES_TO_NAMES
                        }
                    />
                )}
                {selectedCountry === "PE" && (
                    <SchoolFilterPanel
                        selectedSchool={selectedSchool}
                        onSchoolChanged={onSchoolChanged}
                        partnershipOnly={partnershipOnly}
                        compactLayout={compactLayout}
                        isSignupFlow={isSignupFlow}
                        countryCode="PE"
                        adminHierarchy={{
                            root: PERU_ADMIN_LEVELS,
                            levelOneLabel: t`Department `,
                            levelOnePlaceholder: t`e.g., Amazonas`,
                            levelTwoLabel: t`Province`,
                            levelTwoPlaceholder: t`e.g., BAGUA`,
                            localityLabel: t`District`,
                            localityPlaceholder: t`e.g., ARAMANGO`,
                        }}
                        levelOneCodesToNames={
                            PERU_ADMIN_LEVEL_ONE_CODES_TO_NAMES
                        }
                    />
                )}
                {selectedCountry === "PH" && (
                    <SchoolFilterPanel
                        selectedSchool={selectedSchool}
                        onSchoolChanged={onSchoolChanged}
                        partnershipOnly={partnershipOnly}
                        compactLayout={compactLayout}
                        isSignupFlow={isSignupFlow}
                        countryCode="PH"
                        adminHierarchy={{
                            root: PHILIPPINES_ADMIN_LEVELS,
                            levelOneLabel: t`Region`,
                            levelOnePlaceholder: doNotTranslate`e.g., REGION V`,
                            levelTwoLabel: t`Division`,
                            levelTwoPlaceholder: doNotTranslate`e.g., CAMARINES SUR`,
                            localityLabel: t`District`,
                            localityPlaceholder: doNotTranslate`e.g., CARAMOAN`,
                        }}
                        levelOneCodesToNames={
                            PHILIPPINES_ADMIN_LEVEL_ONE_CODES_TO_NAMES
                        }
                    />
                )}
                {selectedCountry === "LOL" && (
                    <LolSchoolFilterPanel
                        selectedSchool={selectedSchool}
                        onSchoolChanged={onSchoolChanged}
                        partnershipOnly={partnershipOnly}
                        compactLayout={compactLayout}
                        isSignupFlow={isSignupFlow}
                    />
                )}
                {selectedCountry === "PK" && (
                    <SchoolFilterPanel
                        selectedSchool={selectedSchool}
                        onSchoolChanged={onSchoolChanged}
                        partnershipOnly={partnershipOnly}
                        compactLayout={compactLayout}
                        isSignupFlow={isSignupFlow}
                        countryCode="PK"
                        adminHierarchy={{
                            root: PAKISTAN_ADMIN_LEVELS,
                            levelOneLabel: t`Province`,
                            levelOnePlaceholder: doNotTranslate`e.g. Sindh`,
                            localityLabel: t`City`,
                            localityPlaceholder: doNotTranslate`e.g. Karachi`,
                        }}
                        levelOneCodesToNames={
                            PAKISTAN_ADMIN_LEVEL_ONE_CODES_TO_NAMES
                        }
                    />
                )}
                {selectedCountry === "VN" && showVietnamFilter && (
                    <SchoolFilterPanel
                        selectedSchool={selectedSchool}
                        onSchoolChanged={onSchoolChanged}
                        partnershipOnly={partnershipOnly}
                        compactLayout={compactLayout}
                        isSignupFlow={isSignupFlow}
                        countryCode="VN"
                        adminHierarchy={{
                            root: VIETNAM_ADMIN_LEVELS,
                            levelOneLabel: t`Province`,
                            levelOnePlaceholder: doNotTranslate`e.g. Bình Dương`,
                            localityLabel: t`District`,
                            localityPlaceholder: doNotTranslate`e.g. Bắc Tân Uyên`,
                        }}
                        levelOneCodesToNames={
                            VIETNAM_ADMIN_LEVEL_ONE_CODES_TO_NAMES
                        }
                    />
                )}
                {selectedCountry === "KE" && showKenyaFilter && (
                    <SchoolFilterPanel
                        selectedSchool={selectedSchool}
                        onSchoolChanged={onSchoolChanged}
                        partnershipOnly={partnershipOnly}
                        compactLayout={compactLayout}
                        isSignupFlow={isSignupFlow}
                        countryCode="KE"
                        adminHierarchy={{
                            root: KENYA_ADMIN_LEVELS,
                            levelOneLabel: t`County`,
                            levelOnePlaceholder: doNotTranslate`e.g. Nairobi`,
                            localityLabel: t`Sub-County`,
                            localityPlaceholder: doNotTranslate`e.g. Embakasi`,
                        }}
                        levelOneCodesToNames={
                            KENYA_ADMIN_LEVEL_ONE_CODES_TO_NAMES
                        }
                    />
                )}
            </React.Suspense>
            {/* sync-end:school-picker-countries */}
        </View>
    );
}

const styles = StyleSheet.create({
    schoolSelector: {minHeight: "auto"},
    compactLayout: {
        display: "grid",
        gap: spacing.small_12,
    },
});

export default SchoolSelector;
